import { Link } from "react-router-dom";
import { Layers, Boxes, GitBranch, Check, ArrowRight } from "lucide-react";
import { Overline, SectionHeading } from "@/components/Section";

const MODELS = [
  {
    icon: Layers, code: "PaaS", title: "Platform-as-a-Service",
    tagline: "We design and ship the whole platform.",
    points: ["Product + brand + architecture", "React / FastAPI / MongoDB full-stack", "Payments, auth, analytics wired in", "Production deploy + handoff docs"],
    tone: "amber",
  },
  {
    icon: Boxes, code: "SaaS", title: "Software-as-a-Service",
    tagline: "Subscription products built to earn.",
    points: ["Recurring billing (Stripe)", "Multi-tenant account model", "Usage analytics + dashboards", "Growth-ready marketing surface"],
    tone: "blue",
  },
  {
    icon: GitBranch, code: "RaaS", title: "Revenue-as-a-Service",
    tagline: "Build together, grow together.",
    points: ["TIB operates the platform for you", "Runs as its own P&L with shared upside", "Ongoing product + infra investment", "You keep ownership — or partner on operations"],
    tone: "mint",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="section-pad border-b border-zinc-900">
        <div className="container-tib">
          <SectionHeading
            overline="Build models"
            title="PaaS, SaaS, RaaS — three ways to build."
            body="Whether you want TIB to ship a platform and hand it over, run it as a subscription business, or build-and-operate it as a shared venture, there's a model for it. You keep ownership of your assets — or partner with TIB to manage and grow them. Your dream, your terms."
          />
        </div>
      </section>

      <section className="section-pad">
        <div className="container-tib grid lg:grid-cols-3 gap-6">
          {MODELS.map((m) => {
            const tones = { amber: "text-tib-amber", blue: "text-tib-blue", mint: "text-tib-mint" };
            return (
              <div key={m.code} className="card-edge p-8 flex flex-col" data-testid={`model-${m.code.toLowerCase()}`}>
                <div className="flex items-center justify-between">
                  <m.icon className={`w-8 h-8 ${tones[m.tone]}`} strokeWidth={1.3} />
                  <span className="font-mono text-xs tracking-widest text-zinc-600">{m.code}</span>
                </div>
                <h3 className="h-serif text-2xl text-white mt-6">{m.title}</h3>
                <p className="text-zinc-400 mt-2">{m.tagline}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {m.points.map((pt) => (
                    <li key={pt} className="flex gap-3 text-sm text-zinc-300">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tones[m.tone]}`} />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* PROCESS */}
      <section className="section-pad bg-tib-ink border-y border-zinc-900">
        <div className="container-tib">
          <SectionHeading overline="How it works" title="From thesis to live platform." className="mb-14" />
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Thesis", d: "You bring the idea or problem. We pressure-test the model." },
              { n: "02", t: "Blueprint", d: "Architecture, scope, and brand. A build plan you sign off on." },
              { n: "03", t: "Ship", d: "Full-stack build on production infrastructure — weeks, not quarters." },
              { n: "04", t: "Operate / Hand off", d: "We run it, or hand you the keys. Your model, your call." },
            ].map((s) => (
              <div key={s.n} data-testid={`process-${s.n}`}>
                <div className="font-mono text-tib-amber text-sm tracking-widest">{s.n}</div>
                <h3 className="h-serif text-2xl text-white mt-3">{s.t}</h3>
                <p className="text-zinc-400 mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad text-center">
        <div className="container-tib">
          <h2 className="h-serif text-4xl md:text-5xl text-white">Pick a model. Bring a thesis.</h2>
          <Link to="/clients/apply" className="btn-primary mt-8 inline-flex" data-testid="services-cta">
            Start a build request <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
